import React, { useState, useEffect } from 'react';
import { X, Search, FileText, Calendar, Users, Tag, ExternalLink } from 'lucide-react';
import { templatesApi } from '../api/client';
import type { PageTemplate, SearchPageTemplatesRequest } from '../schemas/validation';
import { cn } from '../utils/cn';

interface TemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: PageTemplate) => void;
  siteId?: string;
}

export const TemplateLibraryModal: React.FC<TemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  siteId,
}) => {
  const [templates, setTemplates] = useState<PageTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, siteId]);

  // Reload templates when search/filter changes
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        loadTemplates();
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedTags, isOpen]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: SearchPageTemplatesRequest = {
        includePublic: true,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (selectedTags.trim()) {
        params.tags = selectedTags.trim();
      }

      if (siteId) {
        params.siteId = siteId;
      }

      const response = await templatesApi.browseTemplates(params);
      setTemplates(response.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = (template: PageTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTagsArray = (tagsString?: string): string[] => {
    if (!tagsString) return [];
    return tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'modal-backdrop',
        'flex items-center justify-center z-50 p-4',
        isOpen ? 'animate-fade-in' : ''
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'modal-content',
          'max-w-4xl w-full max-h-screen overflow-y-auto',
          isOpen ? 'animate-slide-up' : ''
        )}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FileText className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Page Templates</h2>
              <p className="text-sm text-gray-500 mt-1">Choose a template to start with</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
              'hover:bg-gray-100 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by tags..."
                  value={selectedTags}
                  onChange={e => setSelectedTags(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading templates...</p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || selectedTags
                  ? 'Try adjusting your search or filters.'
                  : 'No templates are available yet.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <div
                  key={template.id}
                  className={cn(
                    'bg-white border border-gray-200 rounded-lg overflow-hidden',
                    'hover:shadow-lg hover:border-primary-300 transition-all duration-200',
                    'cursor-pointer group'
                  )}
                  onClick={() => handleSelectTemplate(template)}
                >
                  {/* Template Preview */}
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <FileText className="h-12 w-12 text-gray-400" />
                    </div>
                    <div className="absolute top-2 right-2">
                      <ExternalLink className="h-4 w-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {template.name}
                    </h3>

                    {template.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {template.description}
                      </p>
                    )}

                    {/* Tags */}
                    {template.tags && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {getTagsArray(template.tags)
                          .slice(0, 3)
                          .map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {tag}
                            </span>
                          ))}
                        {getTagsArray(template.tags).length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{getTagsArray(template.tags).length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Template Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(template.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="h-3 w-3" />
                        <span>{template.usageCount} uses</span>
                      </div>
                    </div>

                    {/* Public indicator */}
                    {template.isPublic && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Public Template
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
